<template>
<div class="user-wizard-wrapper">
  <div class="user-wizard">
    <div class="header">
      Verifica tu perfil
    </div>
    <div class="wizard">
      <div class="navigation">
        <div
          @click="currentStep = 1"
          class="menu-element"
          :class="[ currentStep === 1 ? 'current': '', currentStep > 1 ? 'done' : '' ]">
          <div class="menu-number">1</div>
          <div class="menu-text">Información básica</div>
        </div>
        <div
          @click="currentStep = 2"
          class="menu-element"
          :class="[ currentStep === 2 ? 'current': '', currentStep > 2 ? 'done' : '' ]">
          <div class="menu-number">2</div>
          <div class="menu-text">Documentación oficial</div>
        </div>
        <div
          class="menu-element"
          :class="[ currentStep === 3 ? 'current': '', currentStep > 3 ? 'done' : '' ]">
          <div class="menu-number">3</div>
          <div class="menu-text">Completado</div>
        </div>
        <div class="info">
          <img src="/help/account.svg">
          <div class="info-header">
            ¿Por qué te solicitamos esta información?
          </div>
          <div class="info-content">
            Por cuestiones de prevención de lavado de dinero necesitamos conocerte pero no te preocupes,
            tu información está 100% segura en nuestros servidores donde no puede ser consultada por nadie
            más que nuestro equipo interno para verificarla.
          </div>
        </div>
      </div>
      <div class="content" v-if="currentStep === 1">
        <div class="content-header">
          Información basica
        </div>
        <div class="form">
          <div class="validation-error" v-if="data.curp && !curpVerification">
            El campo CURP no ha podido ser valido, verificalo por favor.
          </div>
          <div class="label">
            <div class="label-text">Fecha de nacimiento</div>
            <datepicker
              v-model="data.birthday"
              initial-view="year"
              input-class="form-input"
              :full-month-name="true"
              :language="es" />
          </div>
          <div class="label">
            <div class="label-text">CURP</div>
            <BaseInput v-model="data.curp" />
          </div>
          <div class="label">
            <div class="label-text">Empresa donde trabajas</div>
            <BaseInput v-model="data.workCompany" />
          </div>
          <div class="label">
            <div class="label-text">Nacionalidad</div>
            <BaseRadio :options="nationalityOptions" v-model="data.nationality" />
          </div>
          <div class="label">
            <div class="label-text">¿Eres una persona políticamente expuesta?</div>
            <BaseRadio :options="booleanOptions" v-model="data.isPep" />
          </div>
          <div class="label">
            <div class="label-text">¿Está relacionado con una persona políticamente expuesta?</div>
            <BaseRadio :options="booleanOptions" v-model="data.isPepRelated" />
          </div>
          <div class="button-wrapper">
            <div
              class="button"
              :class="[ isFirstStepCompleted ? '' : 'disabled' ]"
              @click="() => { if (isFirstStepCompleted) { saveAndNext() } }">
              Guardar
            </div>
          </div>
        </div>
      </div>
      <div class="content" v-if="currentStep === 2">
        <div class="content-header">
          Documentación oficial
        </div>
        <div class="form">
          <div class="id-container">
            <div class="id-header">Identificación oficial</div>
            <div class="id-data">
              <div class="id-subheader">Identificaciones aceptadas:</div>
              <div class="id-option">• INE/IFE</div>
              <div class="id-option">• Pasaporte</div>
              <div class="id-option">• Cédula profesional</div>
              <div class="id-note">
                Los datos que aparecen en tu identificación deben coincidir con los que ingresaste anteriormente en tu perfil, en caso de no coincidir tu cuenta no podrá ser verificada.
              </div>
            </div>
            <div class="id-upload">
              <div class="upload-button">
                <img v-if="idBase64" :src="`data:image/jpg;base64, ${idBase64}`" accept="image/jpeg">
                <img v-else src="/upload-files.svg">
                <input v-if="!loading" type="file" accept="image/*" @change="processFile($event, 'id')"/>
                <div v-else>Cargando...</div>
              </div>
            </div>
          </div>
          <div class="button-wrapper">
            <div class="button" @click="currentStep = 3">
              Guardar
            </div>
          </div>
        </div>
      </div>
      <div class="content" v-if="currentStep === 3">
        <div class="content-header">
          Gracias
        </div>
        <div class="final-text">
          El equipo de Flagship verificará tu información y en breve nos comunicaremos contigo a través
          de tu correo electrónico para notificarte cuando ya puedas acceder a nuestros proyectos premium
          así como, a invertir en tu restaurante favorito.
          <br style="margin-bottom: 20px">
          Mientras tanto puedes visitar nuestro blog para conocer como sacarle más jugo a tus ahorros y
          porqué Flagship es una gran opción para invertir.
        </div>
        <div class="button-wrapper">
          <div class="button" @click="$router.push('/blog')">
            Ir al blog
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import axios from 'axios';
import Datepicker from 'vuejs-datepicker';
import { es } from 'vuejs-datepicker/dist/locale';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseRadio from '@/components/base/BaseRadio.vue';

const { mapState, mapActions } = createNamespacedHelpers('user');

export default {
  data() {
    return {
      es,
      booleanOptions: [
        { text: 'Si', value: true },
        { text: 'No', value: false },
      ],
      nationalityOptions: [
        { text: 'Mexicana', value: 'mexicana' },
        { text: 'Extranjera', value: 'extranjera' },
      ],
      data: {
        curp: null,
      },
      currentStep: 1,
      idBase64: null,
      loading: false,
    };
  },
  computed: {
    isFirstStepCompleted() {
      const verifiedKeys = [
        'birthday',
        'curp',
        'workCompany',
        'nationality',
        'isPep',
        'isPepRelated',
      ];

      for (let i = 0; i < verifiedKeys.length; i += 1) {
        if (this.data[verifiedKeys[i]] === null) {
          return false;
        }
      }
      return this.curpVerification;
    },
    isSecondStepCompleted() {
      if (this.data.officialIdUrl === null) {
        return false;
      }
      return true;
    },
    curpVerification() {
      const re = /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/;
      const isCurpValid = this.data.curp.match(re);

      return isCurpValid;
    },
    ...mapState([
      'user',
    ]),
  },
  methods: {
    saveAndNext() {
      this.updateUser(this.data).then(() => {
        this.currentStep += 1;
      });
    },
    processFile(event, type) {
      const fd = new FormData();
      const file = event.target.files[0];
      const url = `${process.env.VUE_APP_SERVER_URL}/upload/${type}`;
      fd.append('image', file);
      this.loading = true;
      axios.post(url, fd).then(() => {
        this.loadImage();
      });
    },
    loadImage() {
      axios.get(`${process.env.VUE_APP_SERVER_URL}/user/id`).then(({ data }) => {
        this.idBase64 = data;
        this.loading = false;
      });
    },
    ...mapActions([
      'getUser',
      'updateUser',
    ]),
  },
  components: {
    BaseInput,
    Datepicker,
    BaseRadio,
  },
  mounted() {
    this.getUser().then(() => {
      this.data = { ...this.user };
      if (this.data.officialIdUrl) {
        this.loading = true;
        this.loadImage();
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.user-wizard-wrapper {
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background-color: #fafafa;
  font-family: 'Inter', sans-serif;
}
.user-wizard {
  width: 1000px;
  padding: 100px 20px;
  display: flex;
  flex-direction: column;
}
.header {
  width: 100%;
  display: flex;
  padding: 15px 0;
  color: #202124;
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 24px;
}
.wizard {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.navigation {
  width: 27%;
}
.content {
  width: 70%;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background-color: white;
  padding: 24px;
}
.menu-element {
  display: flex;
  align-items: center;
  color: #e0e4e9;

  & + .menu-element {
    margin-top: 10px;
  }
}
.current {
  color: #3479e9;
  .menu-number {
    background-color: #3479e9;
  }
}
.done {
  color: #68c108;
  .menu-number {
    background-color: #68c108;
  }
}
.menu-number {
  background-color: #e0e4e9;
  color: white;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: 14px;
}
.menu-text {
  margin-left: 8px;
}
.content-header {
  margin-bottom: 10px;
  font-size: 22px;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 20px;
}
.form {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.label {
  width: 48%;
  margin-top: 20px;
}
.label--small {
  width: 22%;
}
.label-text {
  line-height: 18px;
  margin-bottom: 12px;
}
/deep/ .form-input {
  font-size: 15px;
  border: none;
  border: 1px solid #e8e8e8;
  padding: 8px;
  font-family: inherit;
  width: 100%;
  border-radius: 4px;

  &:focus {
    border: 1px solid #3479e9;
  }

}
.button-wrapper {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 34px;
}
.button {
  background-color: #3479e9;
  color: white;
  padding: 12px 20px;
  display: flex;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
}
.disabled {
  background-color: lighten($color: #3479e9, $amount: 20);
  cursor: not-allowed;
}
.info {
  margin-top: 40px;
  border-radius: 6px;
  padding: 16px;
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    width: 50%;
  }
}
.info-header {
  font-weight: bolder;
  font-size: 18px;
  line-height: 22px;
  margin-top: 12px;
  width: 100%;
}
.info-content {
  margin-top: 12px;
  line-height: 20px;
  width: 100%;
}
.divisor {
  border-bottom: 1px solid #e8e8e8;
  width: 100%;
  margin-top: 20px;
}
.id-container {
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
}
.id-data {
  width: 50%;
}
.id-header {
  width: 100%;
  font-weight: bolder;
  font-size: 20px;
  margin-bottom: 20px;
}
.id-subheader {
  font-size: 16px;
  margin-bottom: 12px;
}
.id-option {
  & + .id-option {
    margin-top: 8px;
  }
  & + .id-note {
    margin-top: 16px;
  }
}
.id-note {
  line-height: 20px;

  & + .id-subheader {
    margin-top: 20px;
  }
}
.id-upload {
  width: 50%;
  display: flex;
  justify-content: center;
}
.upload-button {
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 15px;

  img {
    width: 100%;
    margin-bottom: 10px;
  }
}
.final-text {
  line-height: 20px;
}
.validation-error {
  width: 100%;
  background: #fae8e7;
  color: #ef5841;
  padding: 16px 10px;
}
@media only screen and (max-width: 850px) {
  .user-wizard {
    width: 100%;
    flex-direction: column;
  }
  .wizard {
    flex-direction: column;
  }
  .navigation {
    width: 100%;
    display: none;
  }
  .content {
    width: 100%;
  }
}
@media only screen and (max-width: 700px) {
  .label {
    width: 100%;
  }
  .id-data {
    width: 100%;
  }
  .id-upload {
    width: 100%;
    margin-top: 25px;
  }
}
</style>
